import { useState, useEffect, useRef } from "react";
import type { Route } from "./+types/brands.how-it-works";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { detectLocale, translations } from "~/i18n/index";
import type { Locale } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta } from "~/lib/seo";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  return { locale };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = (data?.locale ?? "en") as Locale;
  const t = translations[locale] ?? translations.en;
  const m = t.forBrands.howItWorks.meta;
  return [
    { title: m.title },
    { name: "description", content: m.description },
    ...socialMeta({ title: m.title, description: m.description, pathname: "/brands/how-it-works", locale }),
  ];
}

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add("visible"); },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

/* ── Expandable card for acquisition models ── */
function TierCard({
  model,
  index,
  badges,
}: {
  model: { tier: string; label: string; heading: string; description: string; gets: string[]; benefit: string };
  index: number;
  badges: { fullIp: string };
}) {
  const [expanded, setExpanded] = useState(false);
  const isVault = index === 2;

  return (
    <div
      className="relative group"
      style={isVault ? { background: "oklch(0.085 0.004 50)", color: "oklch(0.978 0.008 90)" } : undefined}
    >
      {!isVault && index > 0 ? <div className="hidden lg:block absolute inset-y-0 left-0 w-px bg-border" /> : null}
      <div className="p-8 lg:p-10 h-full flex flex-col">
        {/* Header row */}
        <div className="flex items-center justify-between mb-4">
          <span className="issue-label" style={{ fontFamily: "var(--font-sans)", color: isVault ? "var(--primary)" : undefined, opacity: isVault ? 1 : 0.45 }}>{model.label}</span>
          {isVault && <span className="issue-label px-2 py-0.5" style={{ fontFamily: "var(--font-sans)", border: "1px solid var(--primary)", color: "var(--primary)", fontSize: "0.6rem", letterSpacing: "0.2em" }}>{badges.fullIp}</span>}
        </div>

        {/* Large number */}
        <span className="font-display font-light select-none block mb-3" style={{ fontSize: "clamp(3rem, 5vw, 4.5rem)", lineHeight: 0.9, color: "var(--primary)", opacity: isVault ? 0.5 : 0.25 }}>0{index + 1}</span>

        {/* Heading */}
        <h3 className="font-display font-normal leading-tight mb-4" style={{ fontSize: "clamp(1.4rem, 2vw, 1.9rem)", color: isVault ? "oklch(0.978 0.008 90)" : "var(--foreground)" }}>{model.heading}</h3>

        {/* Benefit tagline — always visible */}
        <p className="issue-label mb-6" style={{ fontFamily: "var(--font-sans)", color: isVault ? "var(--primary)" : "var(--teal-sapphire)", opacity: 0.8 }}>{model.benefit}</p>

        {/* Expandable detail */}
        <div
          className="overflow-hidden transition-all duration-300 ease-out"
          style={{ maxHeight: expanded ? "600px" : "0px", opacity: expanded ? 1 : 0 }}
        >
          <p className="leading-relaxed mb-5" style={{ fontSize: "0.82rem", color: isVault ? "oklch(0.65 0.01 90)" : "var(--muted-foreground)" }}>{model.description}</p>
          <ul className="space-y-2 mb-4">
            {model.gets.map((item, gi) => (
              <li key={gi} className="flex items-start gap-2.5">
                <span className="mt-1.5 h-1 w-1 rounded-full shrink-0" style={{ background: isVault ? "var(--primary)" : "var(--foreground)", opacity: isVault ? 0.8 : 0.35 }} />
                <span style={{ fontSize: "0.78rem", lineHeight: 1.55, color: isVault ? "oklch(0.68 0.01 90)" : "var(--muted-foreground)" }}>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Toggle */}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-auto pt-4 border-t flex items-center gap-2 transition-colors hover:opacity-100"
          style={{ borderColor: isVault ? "oklch(0.22 0.01 50)" : "var(--border)", color: isVault ? "oklch(0.55 0.01 90)" : "var(--muted-foreground)", opacity: 0.65, fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase" as const, fontFamily: "var(--font-sans)" }}
        >
          {expanded ? "Less" : "Details"}
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ transform: expanded ? "rotate(180deg)" : undefined, transition: "transform 200ms" }}>
            <path d="M2 4L5 7L8 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default function HowItWorksPage() {
  const { t } = useT();
  const h = t.forBrands;

  const stepsRef = useReveal(0.08);
  const vsRef = useReveal(0.1);
  const acquireRef = useReveal(0.08);
  const ctaRef = useReveal();

  return (
    <MarketingLayout>

      {/* ══ HERO + 3-STEP PROCESS (merged) ══ */}
      <section className="px-6 lg:px-12 pt-16 lg:pt-24 pb-0 relative">
        {/* Subtle vertical lines */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <line key={i} x1={`${(i + 1) * 11}%`} y1="0" x2={`${(i + 1) * 11}%`} y2="100%" stroke="oklch(0.085 0.004 50)" strokeWidth="1" opacity="0.02" />
          ))}
        </svg>

        <div className="relative">
          <h1 className="font-display font-light leading-[0.88] tracking-tight text-foreground max-w-4xl" style={{ fontSize: "clamp(4rem, 9vw, 8.5rem)" }}>
            {h.howItWorks.headline.split("\n").map((line, i) => (
              <span key={i}>
                {i === 0 ? line : <em className="not-italic" style={{ fontWeight: 300, opacity: 0.35 }}>{line}</em>}
                {i === 0 && <br />}
              </span>
            ))}
          </h1>
          <p className="mt-8 text-muted-foreground leading-relaxed" style={{ fontSize: "clamp(0.95rem, 1.4vw, 1.15rem)", maxWidth: "48ch" }}>{h.howItWorks.intro}</p>
        </div>
      </section>

      {/* ── The 3 steps: oversized numbers with tight copy ── */}
      <section className="px-6 lg:px-12 pt-16 pb-20 lg:pt-20 lg:pb-28">
        <div ref={stepsRef} className="reveal">
          <div className="grid lg:grid-cols-3 gap-0">
            {h.process.items.map((s, i) => (
              <div key={i} className="relative">
                {i > 0 ? <div className="hidden lg:block absolute inset-y-0 left-0 w-px bg-border" /> : null}
                <div className="py-10 lg:py-12" style={{ paddingLeft: i > 0 ? "var(--col-pad)" : undefined, paddingRight: i < h.process.items.length - 1 ? "var(--col-pad)" : undefined }}>
                  {/* Oversized step number */}
                  <span
                    className="font-display font-light select-none block"
                    style={{
                      fontSize: "clamp(5rem, 10vw, 8rem)",
                      lineHeight: 0.8,
                      color: "var(--teal-sapphire)",
                      opacity: 0.12,
                      marginBottom: "-0.15em",
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display font-normal text-foreground leading-tight mb-3" style={{ fontSize: "clamp(1.3rem, 2vw, 1.7rem)" }}>{s.heading}</h3>
                  <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.82rem", maxWidth: "30ch" }}>{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stat strip: replaces quote + bullets ── */}
      <div className="px-6 lg:px-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-y border-border">
          {([
            ["0", "R&D Cost"],
            ["Same Day", "Time to Market"],
            ["Included", "Marketing Assets"],
            ["End-to-End", "Production"],
          ] as [string, string][]).map(([value, label], i) => (
            <div key={label} className={`py-6 ${i > 0 ? "pl-6 border-l border-border" : ""}`}>
              <p className="font-display font-light text-foreground leading-none mb-1" style={{ fontSize: "clamp(1.1rem, 2vw, 1.6rem)" }}>{value}</p>
              <p className="issue-label" style={{ fontFamily: "var(--font-sans)", opacity: 0.4 }}>{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ══ VS TRADITIONAL — dramatic two-tone ══ */}
      <section className="px-6 lg:px-12 py-20 lg:py-28">
        <div ref={vsRef} className="reveal">
          <span className="issue-label block mb-10" style={{ fontFamily: "var(--font-sans)" }}>{h.vs.overline}</span>

          {/* Desktop: dramatic side-by-side */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-[1fr_1fr] gap-0 rounded-xl overflow-hidden border border-border">
              {/* Traditional side — muted */}
              <div className="p-10 lg:p-12" style={{ background: "oklch(0.06 0.003 50)" }}>
                <span className="issue-label block mb-8" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.vs.traditional}</span>
                <div className="space-y-6">
                  {h.vs.rows.map(([attr, traditional]) => (
                    <div key={attr}>
                      <span className="issue-label block mb-1.5" style={{ fontFamily: "var(--font-sans)", opacity: 0.3, fontSize: "0.55rem" }}>{attr}</span>
                      <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.82rem", opacity: 0.6 }}>{traditional}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Caratzon side — vibrant */}
              <div className="p-10 lg:p-12 border-l border-border relative">
                <span className="issue-label block mb-8" style={{ fontFamily: "var(--font-sans)", color: "var(--teal-sapphire)" }}>{h.vs.caratzon}</span>
                <div className="space-y-6">
                  {h.vs.rows.map(([attr, , caratzon]) => (
                    <div key={attr}>
                      <span className="issue-label block mb-1.5" style={{ fontFamily: "var(--font-sans)", opacity: 0.45, fontSize: "0.55rem" }}>{attr}</span>
                      <p className="text-foreground leading-relaxed" style={{ fontSize: "0.82rem" }}>{caratzon}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile: stacked cards */}
          <div className="lg:hidden space-y-0">
            {h.vs.rows.map(([attr, traditional, caratzon]) => (
              <div key={attr}>
                <div className="rule" />
                <div className="py-5 space-y-3">
                  <span className="issue-label block" style={{ fontFamily: "var(--font-sans)", opacity: 0.55 }}>{attr}</span>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="issue-label block mb-1" style={{ fontFamily: "var(--font-sans)", opacity: 0.3, fontSize: "0.5rem" }}>{h.vs.traditional}</span>
                      <p className="text-muted-foreground" style={{ fontSize: "0.8rem", lineHeight: 1.5, opacity: 0.6 }}>{traditional}</p>
                    </div>
                    <div>
                      <span className="issue-label block mb-1" style={{ fontFamily: "var(--font-sans)", fontSize: "0.5rem", color: "var(--teal-sapphire)" }}>{h.vs.caratzon}</span>
                      <p className="text-foreground" style={{ fontSize: "0.8rem", lineHeight: 1.5 }}>{caratzon}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="rule" />
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ ACQUISITION MODELS — expandable cards ══ */}
      <section id="acquire" className="px-6 lg:px-12 py-16 lg:py-24 relative">
        <div ref={acquireRef} className="reveal">
          <div className="flex items-baseline justify-between mb-2">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.acquire.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.acquire.subtitle}</span>
          </div>

          <h2 className="font-display font-light text-foreground mb-14" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", lineHeight: 1, maxWidth: "22ch" }}>
            {h.acquire.heading.split("\n").map((line, i) => (
              <span key={i}>{i === 0 ? line : <em className="not-italic" style={{ opacity: 0.4 }}>{line}</em>}{i === 0 && <br />}</span>
            ))}
          </h2>

          <div className="grid lg:grid-cols-3 gap-0 border border-border">
            {h.acquire.models.map((m, i) => (
              <TierCard key={m.tier} model={m} index={i} badges={h.badges} />
            ))}
          </div>

          {/* IP notes — compact, inline */}
          <div className="mt-8 grid lg:grid-cols-2 gap-4">
            <div className="flex gap-3 items-start py-3">
              <span className="mt-1 h-1.5 w-1.5 rounded-full shrink-0 bg-muted-foreground/30" />
              <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.75rem" }}>
                <strong className="text-foreground/60">{h.acquire.ipNotes.standardLabel}:</strong>{" "}
                {h.acquire.ipNotes.standardBody}
              </p>
            </div>
            <div className="flex gap-3 items-start py-3">
              <span className="mt-1 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: "var(--primary)", opacity: 0.5 }} />
              <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.75rem" }}>
                <strong style={{ color: "var(--primary)", opacity: 0.7 }}>{h.acquire.ipNotes.vaultLabel}:</strong>{" "}
                {h.acquire.ipNotes.vaultBody}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ CARATZON STUDIO — streamlined ══ */}
      <section
        className="relative"
        style={{ background: "oklch(0.10 0.004 50)", color: "oklch(0.92 0.008 90)" }}
      >
        <div className="px-6 lg:px-12 py-20 lg:py-28 max-w-6xl mx-auto">
          <span
            className="issue-label block mb-5"
            style={{ fontFamily: "var(--font-sans)", color: "oklch(0.55 0.06 52)" }}
          >
            {h.studio.overline}
          </span>

          <h2
            className="font-display font-light leading-[0.95] tracking-tight mb-16"
            style={{ fontSize: "clamp(2.2rem, 5vw, 4rem)", color: "oklch(0.95 0.006 90)" }}
          >
            {h.studio.heading.split("\n").map((line: string, i: number) => (
              <span key={i}>
                {i === 0 ? line : <em className="not-italic" style={{ opacity: 0.4 }}>{line}</em>}
                {i === 0 && <br />}
              </span>
            ))}
          </h2>

          <div className="grid lg:grid-cols-2 gap-0 rounded-xl overflow-hidden border" style={{ borderColor: "oklch(0.20 0.01 50)" }}>
            {/* Essentials Kit */}
            <div className="p-8 lg:p-10" style={{ background: "oklch(0.13 0.004 50)", borderRight: "1px solid oklch(0.20 0.01 50)" }}>
              <div className="flex items-center gap-3 mb-8">
                <h3 className="font-display font-light" style={{ fontSize: "1.4rem", color: "oklch(0.95 0.006 90)" }}>{h.studio.essentials.heading}</h3>
                <span className="inline-block px-2.5 py-0.5 rounded-full" style={{ fontSize: "0.6rem", letterSpacing: "0.06em", background: "oklch(0.45 0.08 213 / 0.15)", color: "oklch(0.65 0.08 213)" }}>{h.studio.essentials.badge}</span>
              </div>
              <ul className="space-y-4">
                {h.studio.essentials.features.map((f: string) => (
                  <li key={f} className="flex items-start gap-3">
                    <svg className="shrink-0 mt-0.5" style={{ color: "oklch(0.55 0.08 213)" }} width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span style={{ fontSize: "0.85rem", color: "oklch(0.75 0.01 90)", lineHeight: 1.5 }}>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Caratzon Kit */}
            <div className="p-8 lg:p-10" style={{ background: "oklch(0.15 0.006 50)" }}>
              <div className="flex items-center gap-3 mb-8">
                <h3 className="font-display font-light" style={{ fontSize: "1.4rem", color: "oklch(0.95 0.006 90)" }}>{h.studio.caratzonKit.heading}</h3>
                <span className="inline-block px-2.5 py-0.5 rounded-full" style={{ fontSize: "0.6rem", letterSpacing: "0.06em", background: "oklch(0.48 0.072 52 / 0.2)", color: "oklch(0.72 0.06 52)" }}>{h.studio.caratzonKit.badge}</span>
              </div>
              <ul className="space-y-4">
                {h.studio.caratzonKit.features.map((f: string) => (
                  <li key={f} className="flex items-start gap-3">
                    <svg className="shrink-0 mt-0.5" style={{ color: "oklch(0.65 0.06 52)" }} width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span style={{ fontSize: "0.85rem", color: "oklch(0.75 0.01 90)", lineHeight: 1.5 }}>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ CTA ══ */}
      <section className="px-6 lg:px-12 py-20 lg:py-28">
        <div ref={ctaRef} className="reveal grid lg:grid-cols-[1fr_auto] items-end gap-8">
          <div>
            <span className="issue-label block mb-5" style={{ fontFamily: "var(--font-sans)" }}>{h.cta.overline}</span>
            <h2 className="font-display font-light leading-[0.92] tracking-tight text-foreground" style={{ fontSize: "clamp(2.8rem, 6vw, 5.5rem)" }}>
              {h.cta.heading.split("\n").map((line, i) => (
                <span key={i}>{i === 0 ? line : <em className="not-italic" style={{ fontWeight: 300, opacity: 0.45 }}>{line}</em>}{i === 0 && <br />}</span>
              ))}
            </h2>
          </div>
          <div className="pb-2 space-y-4">
            <a href="/sign-in?redirect=/trade/catalog" className="block px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors text-center" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>{h.cta.primary}</a>
            <p className="text-center text-muted-foreground" style={{ fontSize: "0.7rem" }}>{h.cta.tradeOnly}</p>
          </div>
        </div>
      </section>

    </MarketingLayout>
  );
}
